import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { formatMarkdownToSafeHtml } from "@/lib/sanitize";

interface AgentInsightsPreviewProps {
  northId: string;
  primaryColor?: string | null;
}

export const AgentInsightsPreview = ({ northId, primaryColor }: AgentInsightsPreviewProps) => {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['agent-insights-preview', northId],
    queryFn: async () => {
      // For demo report, always fetch from the actual demo north report
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      const targetNorthId = isDemo ? 'd0daa852-5636-45c6-8a29-0d12ba9cf2d1' : northId;

      const { data, error } = await supabase
        .from('agent_insights')
        .select('*')
        .eq('north_id', targetNorthId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Use the actual alyssum insights text as fallback
  const demoSummary = `Interest in the listing is currently slow, despite positive buyer sentiment stemming from feedback that indicates buyer enthusiasm for the home's overall condition and features. While the price is assessed as competitive, the lack of showings highlights a disconnect between online engagement and physical visits, suggesting buyers may be hesitant to take the next step. Feedback themes remain scattered, yet the home's good condition is a plus, while marketing is performing moderately but not converting to showings as effectively as desired. With activity expected to remain flat and the seller holding medium flexibility with low urgency, the current market signals suggest a need to re-evaluate the barriers preventing in-person engagement. This is critical for activating the positive sentiment into concrete offers. The current positioning indicates a solid foundation, yet a strategic adjustment may be needed to align with buyer actions more closely.`;

  const summaryText = insights?.ai_summary || demoSummary;

  return (
    <section className="mb-8">
      <Card style={{ borderColor: primaryColor ? `${primaryColor}20` : undefined }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: primaryColor || '#DAA520' }}>
            <Sparkles className="h-6 w-6" />
            Agent Insights & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-3">
          <div
            className="text-foreground/90 leading-relaxed text-base"
            dangerouslySetInnerHTML={{
              __html: formatMarkdownToSafeHtml(summaryText)
            }}
          />
        </CardContent>
      </Card>
    </section>
  );
};
